function _createForOfIteratorHelper(r, e) {
  var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (!t) {
    if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) {
      t && (r = t);
      var n = 0,
        F = function () {};
      return {
        s: F,
        n: function () {
          return n >= r.length ? {
            done: !0
          } : {
            done: !1,
            value: r[n++]
          };
        },
        e: function (r) {
          throw r;
        },
        f: F
      };
    }
    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }
  var o,
    a = !0,
    u = !1;
  return {
    s: function () {
      t = t.call(r);
    },
    n: function () {
      var r = t.next();
      return a = r.done, r;
    },
    e: function (r) {
      u = !0, o = r;
    },
    f: function () {
      try {
        a || null == t.return || t.return();
      } finally {
        if (u) throw o;
      }
    }
  };
}
import PropTypes from "prop-types";
function _unsupportedIterableToArray(r, a) {
  if (r) {
    if ("string" == typeof r) return _arrayLikeToArray(r, a);
    var t = {}.toString.call(r).slice(8, -1);
    return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0;
  }
}
function _arrayLikeToArray(r, a) {
  (null == a || a > r.length) && (a = r.length);
  for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e];
  return n;
}
/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { createElement, useEffect, useImperativeHandle, useMemo, useState, useCallback, useRef, forwardRef } from 'react';
import unified from 'unified';
import classNames from 'classnames';
import MarkdownActions, { insertText } from './markdown_actions';
import { OuiMarkdownEditorToolbar } from './markdown_editor_toolbar';
import { OuiMarkdownEditorTextArea } from './markdown_editor_text_area';
import { OuiMarkdownFormat } from './markdown_format';
import { OuiMarkdownEditorDropZone } from './markdown_editor_drop_zone';
import { htmlIdGenerator } from '../../services/';
import { MODE_EDITING, MODE_VIEWING } from './markdown_modes';
import { OuiModal } from '../modal';
import { OuiMarkdownContext } from './markdown_context';
import * as MarkdownTooltip from './plugins/markdown_tooltip';
import { defaultParsingPlugins, defaultProcessingPlugins, defaultUiPlugins } from './plugins/markdown_default_plugins';
import { OuiResizeObserver } from '../observer/resize_observer';
// TODO I wanted to use the useCombinedRefs
// but I can't because it's not allowed to use react hooks
// inside a callback.
var mergeRefs = function mergeRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  var filteredRefs = refs.filter(Boolean);
  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 0) return filteredRefs[0];
  return function (inst) {
    var _iterator = _createForOfIteratorHelper(filteredRefs),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var ref = _step.value;
        if (typeof ref === 'function') {
          ref(inst);
        } else if (ref) {
          ref.current = inst;
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  };
};
function isNewLine(char) {
  if (char == null) return true;
  return !!char.match(/[\r\n]/);
}
function padWithNewlinesIfNeeded(textarea, text) {
  var selectionStart = textarea.selectionStart;
  var selectionEnd = textarea.selectionEnd;

  // block parsing requires two leading new lines and none trailing, but we add an extra trailing line for readability
  var isPrevNewLine = isNewLine(textarea.value[selectionStart - 1]);
  var isPrevPrevNewLine = isNewLine(textarea.value[selectionStart - 2]);
  var isNextNewLine = isNewLine(textarea.value[selectionEnd]);

  // pad text with newlines as needed
  text = "".concat(isPrevNewLine ? '' : '\n').concat(isPrevPrevNewLine ? '' : '\n').concat(text).concat(isNextNewLine ? '' : '\n');
  return text;
}
export var OuiMarkdownEditor = forwardRef(({
  className,
  editorId: _editorId,
  value,
  onChange,
  height = 250,
  maxHeight = 500,
  autoExpandPreview = true,
  parsingPluginList = defaultParsingPlugins,
  processingPluginList = defaultProcessingPlugins,
  uiPlugins = defaultUiPlugins,
  onParse,
  errors = [],
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  initialViewMode = MODE_EDITING,
  dropHandlers = [],
  ...rest
}, ref) => {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const editorId = useMemo(() => _editorId || htmlIdGenerator()(), [_editorId]);
  const [pluginEditorPlugin, setPluginEditorPlugin] = useState(undefined);
  const toolbarPlugins = [...uiPlugins];
  // @ts-ignore __originatedFromOui is a custom property
  if (!uiPlugins.__originatedFromOui) {
    toolbarPlugins.unshift(MarkdownTooltip.plugin);
    console.warn('Deprecation warning: uiPlugins passed to OuiMarkdownEditor does not include the tooltip plugin, which has been added for you. This automatic inclusion has been deprecated and will be removed in the future, see https://github.com/elastic/eui/pull/4383');
  }
  const markdownActions = useMemo(() => new MarkdownActions(editorId, toolbarPlugins),
  // toolbarPlugins _is_ accounted for
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [editorId, toolbarPlugins.map(({
    name
  }) => name).join(',')]);
  const parser = useMemo(() => {
    const Compiler = tree => {
      return tree;
    };
    function identityCompiler() {
      this.Compiler = Compiler;
    }
    return unified().use(parsingPluginList).use(identityCompiler);
  }, [parsingPluginList]);
  const [parsed, parseError] = useMemo(() => {
    try {
      const parsed = parser.processSync(value);
      return [parsed, null];
    } catch (e) {
      return [null, e];
    }
  }, [parser, value]);
  const isPreviewing = viewMode === MODE_VIEWING;
  const isEditing = viewMode === MODE_EDITING;
  const replaceNode = useCallback((position, next) => {
    const leading = value.substr(0, position.start.offset);
    const trailing = value.substr(position.end.offset);
    onChange(`${leading}${next}${trailing}`);
  }, [value, onChange]);
  const contextValue = useMemo(() => ({
    openPluginEditor: plugin => setPluginEditorPlugin(() => plugin),
    replaceNode
  }), [replaceNode]);
  const [selectedNode, setSelectedNode] = useState();
  const textareaRef = useRef(null);
  useEffect(() => {
    if (textareaRef == null) return;
    if (parsed == null) return;
    const getCursorNode = () => {
      const {
        selectionStart
      } = textareaRef.current;
      let node = parsed.result ?? parsed.contents;
      outer: while (true) {
        if (node.children) {
          for (let i = 0; i < node.children.length; i++) {
            const child = node.children[i];
            if (child.position.start.offset < selectionStart && selectionStart < child.position.end.offset) {
              if (child.type === 'text') break outer; // don't dive into `text` nodes
              node = child;
              continue outer;
            }
          }
        }
        break;
      }
      setSelectedNode(node);
    };
    const textarea = textareaRef.current;
    textarea.addEventListener('keyup', getCursorNode);
    textarea.addEventListener('mouseup', getCursorNode);
    return () => {
      textarea.removeEventListener('keyup', getCursorNode);
      textarea.removeEventListener('mouseup', getCursorNode);
    };
  }, [parsed]);
  useEffect(() => {
    if (onParse) {
      const messages = parsed ? parsed.messages : [];
      const ast = parsed ? parsed.result ?? parsed.contents : null;
      onParse(parseError, {
        messages,
        ast
      });
    }
  }, [onParse, parsed, parseError]);
  useImperativeHandle(ref, () => ({
    textarea: textareaRef.current,
    replaceNode
  }), [replaceNode]);
  const textarea = textareaRef.current;
  const previewRef = useRef(null);
  const editorToolbarRef = useRef(null);
  const [hasUnacceptedItems, setHasUnacceptedItems] = React.useState(false);
  const [currentHeight, setCurrentHeight] = useState(height);
  const [editorFooterHeight, setEditorFooterHeight] = useState(0);
  const [editorToolbarHeight, setEditorToolbarHeight] = useState(0);
  const classes = classNames('ouiMarkdownEditor', {
    'ouiMarkdownEditor--fullHeight': height === 'full'
  }, {
    'ouiMarkdownEditor--isPreviewing': isPreviewing
  }, className);
  const onResize = () => {
    if (textarea && isEditing && height !== 'full') {
      const resizedTextareaHeight = textarea.offsetHeight + editorFooterHeight;
      setCurrentHeight(resizedTextareaHeight);
    }
  };
  useEffect(() => {
    setEditorToolbarHeight(editorToolbarRef.current.offsetHeight);
  }, [setEditorToolbarHeight]);
  useEffect(() => {
    if (isPreviewing && autoExpandPreview && height !== 'full') {
      if (previewRef.current.scrollHeight > currentHeight) {
        // scrollHeight does not include the border or margin
        // so we ask for the computed value for those,
        // which is always in pixels because getComputedValue
        // returns the resolved values
        const elementComputedStyle = window.getComputedStyle(previewRef.current);
        const borderWidth = parseFloat(elementComputedStyle.borderTopWidth) + parseFloat(elementComputedStyle.borderBottomWidth);
        const marginWidth = parseFloat(elementComputedStyle.marginTop) + parseFloat(elementComputedStyle.marginBottom);

        // then add an extra pixel for safety and because the scrollHeight value is rounded
        const extraHeight = borderWidth + marginWidth + 1;
        setCurrentHeight(previewRef.current.scrollHeight + extraHeight);
      }
    }
  }, [currentHeight, isPreviewing, height, autoExpandPreview]);
  const previewHeight = height === 'full' ? `calc(100% - ${editorFooterHeight}px)` : currentHeight;
  const textAreaHeight = height === 'full' ? '100%' : `calc(${height - editorFooterHeight}px)`;
  const textAreaMaxHeight = height !== 'full' ? `${maxHeight - editorFooterHeight}px` : '';

  // safari needs this calc when the height is set to full
  const editorToggleContainerHeight = `calc(100% - ${editorToolbarHeight}px)`;
  return <OuiMarkdownContext.Provider value={contextValue}>
        <div className={classes} {...rest}>
          <OuiMarkdownEditorToolbar ref={editorToolbarRef} selectedNode={selectedNode} markdownActions={markdownActions} onClickPreview={() => setViewMode(isPreviewing ? MODE_EDITING : MODE_VIEWING)} viewMode={viewMode} uiPlugins={toolbarPlugins} />

          {isPreviewing && <div ref={previewRef} className="ouiMarkdownEditorPreview" style={{
        height: previewHeight
      }}>
              <OuiMarkdownFormat parsingPluginList={parsingPluginList} processingPluginList={processingPluginList}>
                {value}
              </OuiMarkdownFormat>
            </div>}
          {/* Toggle the editor's display instead of unmounting to retain its undo/redo history */}
          <div className="ouiMarkdownEditor__toggleContainer" style={{
        height: editorToggleContainerHeight
      }}>
            <OuiMarkdownEditorDropZone setEditorFooterHeight={setEditorFooterHeight} isEditing={isEditing} dropHandlers={dropHandlers} insertText={(text, config) => {
          if (config.block) {
            text = padWithNewlinesIfNeeded(textareaRef.current, text);
          }
          const originalSelectionStart = textareaRef.current.selectionStart;
          const newSelectionPoint = originalSelectionStart + text.length;
          insertText(textareaRef.current, {
            text,
            selectionStart: newSelectionPoint,
            selectionEnd: newSelectionPoint
          });
        }} uiPlugins={toolbarPlugins} errors={errors} hasUnacceptedItems={hasUnacceptedItems} setHasUnacceptedItems={setHasUnacceptedItems}>
              <OuiResizeObserver onResize={onResize}>
                {resizeRef => {
              return <OuiMarkdownEditorTextArea height={textAreaHeight} maxHeight={textAreaMaxHeight} ref={mergeRefs(textareaRef, resizeRef)} id={editorId} onChange={e => onChange(e.target.value)} value={value} onFocus={() => setHasUnacceptedItems(false)} {...{
                'aria-label': ariaLabel,
                'aria-labelledby': ariaLabelledBy,
                'aria-describedby': ariaDescribedBy
              }} />;
            }}
              </OuiResizeObserver>
            </OuiMarkdownEditorDropZone>

            {pluginEditorPlugin && <OuiModal onClose={() => setPluginEditorPlugin(undefined)}>
                {createElement(pluginEditorPlugin.editor, {
            node: selectedNode && selectedNode.type === pluginEditorPlugin.name ? selectedNode : null,
            onCancel: () => setPluginEditorPlugin(undefined),
            onSave: (markdown, config) => {
              if (selectedNode && selectedNode.type === pluginEditorPlugin.name) {
                // modifying an existing node
                textareaRef.current.setSelectionRange(selectedNode.position.start.offset, selectedNode.position.end.offset);
              } else {
                // creating a new node
                if (config.block) {
                  // inject newlines if needed
                  markdown = padWithNewlinesIfNeeded(textareaRef.current, markdown);
                }
              }
              insertText(textareaRef.current, {
                text: markdown,
                selectionStart: undefined,
                selectionEnd: undefined
              });
              setPluginEditorPlugin(undefined);
            }
          })}
              </OuiModal>}
          </div>
        </div>
      </OuiMarkdownContext.Provider>;
});
OuiMarkdownEditor.propTypes = {
  className: PropTypes.string,
  /** aria-label OR aria-labelledby must be set */"aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  /** aria-label OR aria-labelledby must be set */"aria-labelledby": PropTypes.string,
  /** ID of an element describing the text editor, useful for associating error messages */"aria-describedby": PropTypes.string,
  /** a unique ID to attach to the textarea. If one isn't provided, a random one
       * will be generated */
  editorId: PropTypes.string,
  /** A markdown content */value: PropTypes.string.isRequired,
  /** callback function when markdown content is modified */onChange: PropTypes.func.isRequired,
  /**
       * Sets the `height` in pixels of the editor/preview area or pass `full` to allow
       * the OuiMarkdownEditor to fill the height of its container.
       * When in `full` mode the vertical resize is not allowed.
       */
  height: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.oneOf(["full"])]),
  /**
       * Sets the `max-height` in pixels of the editor/preview area.
       * It has no effect when the `height` is set to `full`.
       */
  maxHeight: PropTypes.number,
  /**
       * Automatically adjusts the preview height to fit all the content and avoid a scrollbar.
       */
  autoExpandPreview: PropTypes.bool,
  /** plugins to identify new syntax and parse it into an AST node */parsingPluginList: PropTypes.any,
  /** plugins to process the markdown AST nodes into a React nodes */processingPluginList: PropTypes.any,
  /** defines UI for plugins' buttons in the toolbar as well as any modals or extra UI that provides content to the editor */uiPlugins: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    button: PropTypes.shape({
      label: PropTypes.string.isRequired,
      iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addBookmark", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "analyzeEvent", "annotation", "anomalyDetection", "apmApp", "apmTrace", "apps", "appSearchApp", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "bookOpen", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "chatLeft", "chatRight", "check", "checkInCircleEmpty", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "color", "compass", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "crossInCircleEmpty", "crossInCircleFilled", "currency", "cut", "dashboard", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentation", "documentEdit", "documents", "dot", "download", "dockedBottom", "dockedDetached", "dockedLeft", "dockedRight", "dockedTakeover", "dockedTop", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "functionAdd", "gear", "generate", "generateContent", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "history", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "integrationGeneral", "integrationObservability", "integrationSearch", "integrationSecurity", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lineChart", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOpenSearch", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "polygon", "popout", "power", "pulse", "push", "questionInCircle", "quote", "radar", "radius", "recent", "recentlyViewedApp", "redeploy", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "rocket", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sparkleFilled", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "thumbsDown", "thumbsUp", "timeline", "timelionApp", "timeslider", "training", "trash", "upgradeAssistantApp", "uptimeApp", "undeploy", "unfold", "unlink", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visBuilder", "visBuilderSavedObject", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visQueryDQL", "visQueryPPL", "visQueryPromQL", "visQuerySQL", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram", "wsAnalytics", "wsEssentials", "wsObservability", "wsSearch", "wsSecurityAnalytics", "wsSelector", "navAdministration", "navAiFlow", "navAlerting", "navAnomalyDetection", "navDashboards", "navData", "navDetectionRules", "navDevtools", "navDiscover", "navExperiments", "navGetStarted", "navInfo", "navInfra", "navIntegrations", "navJudgements", "navManage", "navMaps", "navModels", "navNotebooks", "navNotifications", "navOverview", "navQuerySets", "navReports", "navSecurityCases", "navSecurityFindings", "navServiceMap", "navServices", "navSlos", "navThreatIntel", "navTicketing", "navUi", "navSearchConfigurationsln"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired
    }).isRequired,
    helpText: PropTypes.node,
    formatting: PropTypes.shape({
      prefix: PropTypes.string,
      suffix: PropTypes.string,
      blockPrefix: PropTypes.string,
      blockSuffix: PropTypes.string,
      multiline: PropTypes.bool,
      replaceNext: PropTypes.string,
      prefixSpace: PropTypes.bool,
      scanFor: PropTypes.string,
      surroundWithNewlines: PropTypes.bool,
      orderedList: PropTypes.bool,
      trimFirst: PropTypes.bool
    }),
    editor: PropTypes.elementType
  }).isRequired),
  /** errors to bubble up */errors: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.any.isRequired, PropTypes.any.isRequired]).isRequired),
  /** callback triggered when parsing results are available */onParse: PropTypes.func,
  /** initial display mode for the editor */initialViewMode: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.any.isRequired]),
  /** array defining any drag&drop handlers */dropHandlers: PropTypes.arrayOf(PropTypes.shape({
    supportedFiles: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    accepts: PropTypes.func.isRequired,
    getFormattingForItem: PropTypes.func.isRequired
  }).isRequired)
};
OuiMarkdownEditor.displayName = 'OuiMarkdownEditor';
try {
  OuiMarkdownEditor.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src/components/markdown_editor/markdown_editor.tsx',
    description: '',
    displayName: 'OuiMarkdownEditor',
    methods: [],
    props: {
      value: {
        defaultValue: null,
        description: 'A markdown content',
        name: 'value',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'callback function when markdown content is modified',
        name: 'onChange',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: '(value: string) => void'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      height: {
        defaultValue: {
          value: '250'
        },
        description: 'Sets the `height` in pixels of the editor/preview area or pass `full` to allow\n' + 'the OuiMarkdownEditor to fill the height of its container.\n' + 'When in `full` mode the vertical resize is not allowed.',
        name: 'height',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number | "full"'
        }
      },
      maxHeight: {
        defaultValue: {
          value: '500'
        },
        description: 'Sets the `max-height` in pixels of the editor/preview area.\n' + 'It has no effect when the `height` is set to `full`.',
        name: 'maxHeight',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      editorId: {
        defaultValue: null,
        description: "a unique ID to attach to the textarea. If one isn't provided, a random one\n" + 'will be generated',
        name: 'editorId',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      autoExpandPreview: {
        defaultValue: {
          value: 'true'
        },
        description: 'Automatically adjusts the preview height to fit all the content and avoid a scrollbar.',
        name: 'autoExpandPreview',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      parsingPluginList: {
        defaultValue: {
          value: 'getDefaultOuiMarkdownParsingPlugins()'
        },
        description: 'plugins to identify new syntax and parse it into an AST node',
        name: 'parsingPluginList',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'PluggableList<Settings>'
        }
      },
      processingPluginList: {
        defaultValue: {
          value: 'getDefaultOuiMarkdownProcessingPlugins()'
        },
        description: 'plugins to process the markdown AST nodes into a React nodes',
        name: 'processingPluginList',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'PluggableList<Settings>'
        }
      },
      uiPlugins: {
        defaultValue: {
          value: 'getDefaultOuiMarkdownUiPlugins()'
        },
        description: "defines UI for plugins' buttons in the toolbar as well as any modals or extra UI that provides content to the editor",
        name: 'uiPlugins',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'OuiMarkdownEditorUiPlugin<any>[]'
        }
      },
      errors: {
        defaultValue: {
          value: '[]'
        },
        description: 'errors to bubble up',
        name: 'errors',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'OuiMarkdownParseError[]'
        }
      },
      onParse: {
        defaultValue: null,
        description: 'callback triggered when parsing results are available',
        name: 'onParse',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(error: OuiMarkdownParseError, data: { messages: VFileMessage[]; ast: OuiMarkdownAstNode; }) => void'
        }
      },
      initialViewMode: {
        defaultValue: {
          value: "'editing' as const"
        },
        description: 'initial display mode for the editor',
        name: 'initialViewMode',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'MARKDOWN_MODE',
          value: [{
            value: '"editing"'
          }, {
            value: '"viewing"'
          }]
        }
      },
      dropHandlers: {
        defaultValue: {
          value: '[]'
        },
        description: 'array defining any drag&drop handlers',
        name: 'dropHandlers',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'OuiMarkdownDropHandler[]'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + 'aria-label OR aria-labelledby must be set\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      ref: {
        defaultValue: null,
        description: '',
        name: 'ref',
        parent: {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'Ref<OuiMarkdownEditorRef>'
        }
      }
    },
    extendedInterfaces: ['HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps', 'Attributes']
  };
} catch (e) {}